import { useState } from 'react';
import type { AsignarUnidadRequest } from '@condo/contracts';
import { useUnidades } from '@/features/unidades/api';
import { useAsignarUnidad } from '@/features/residentes/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  condoId: string;
  residenteId: string;
}

export function AsignarUnidadDialog({
  open,
  onOpenChange,
  condoId,
  residenteId,
}: Props) {
  const unidadesQ = useUnidades(condoId, { activo: true });
  const asignar = useAsignarUnidad(condoId, residenteId);

  const [unidadId, setUnidadId] = useState('');
  const [rol, setRol] = useState<AsignarUnidadRequest['rol']>('propietario');
  const [habita, setHabita] = useState(true);
  const [fechaInicio, setFechaInicio] = useState(
    new Date().toISOString().slice(0, 10),
  );

  const submit = () => {
    if (!unidadId) return;
    asignar.mutate(
      { unidadId, rol, habita, fechaInicio },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Asignar unidad</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="eyebrow">Unidad</label>
            <select
              className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
              value={unidadId}
              onChange={(e) => setUnidadId(e.target.value)}
            >
              <option value="">Seleccione…</option>
              {unidadesQ.data?.items.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.numero} ({u.tipo})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="eyebrow">Rol</label>
            <select
              className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
              value={rol}
              onChange={(e) => setRol(e.target.value as AsignarUnidadRequest['rol'])}
            >
              <option value="propietario">Propietario</option>
              <option value="arrendatario">Arrendatario</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={habita}
              onChange={(e) => setHabita(e.target.checked)}
            />
            Habita la unidad
          </label>
          <div>
            <label className="eyebrow">Fecha inicio</label>
            <input
              type="date"
              className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
            />
          </div>
          {asignar.isError && (
            <p className="text-sm text-[var(--color-danger)]">
              {(asignar.error as Error).message}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={!unidadId || asignar.isPending}>
            {asignar.isPending ? 'Asignando…' : 'Asignar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
